/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Modal } from '../../../shared/modals/Modal';
import { Pressable, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useModalBackdropPadding } from '../../../shared/ui/hooks-ui/useModalBackdropPadding';
import { useContentLayout } from '../../../shared/layout/useContentLayout';
import { spacing } from '../../../shared/theme/spacing'
import { webOperationalScrollBindings } from '../../../shared/theme/webScrollbar';
import { settingsStyles as styles } from '../../../settings/screens/settingsScreen.styles';
import { ModalScreenHeader } from '../../../shared/modals/ModalScreenHeader';
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { modalShellShared } from '../../../shared/modals/modalShellShared.styles';
import { posSharedUi as posUi } from '../../../shared/ui/posSharedUi';
import { cashStyles } from './cashManagement.styles';

type Props = {
  visible: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footerLabel?: string;
  wide?: boolean;
};

export function CashOperationalSheetModal({
  visible,
  title,
  subtitle,
  onClose,
  children,
  footerLabel = 'Listo',
  wide,
}: Props) {
  const backdropPadding = useModalBackdropPadding();
  const { layoutWebShell } = useContentLayout('dataWide');
  const mobile = !layoutWebShell;

  // ── Mobile: bottom sheet (View + absoluteFill — Pressable envolviendo el card bloquea scroll) ──
  if (mobile) {
    return (
      <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose} accessibilityViewIsModal>
        <View style={[posUi.modalBackdrop, { justifyContent: 'flex-end', padding: 0 }]}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <View style={[cashStyles.sheetMobileContainer, { maxHeight: '92%', width: '100%' }]}>
            <View style={cashStyles.sheetMobileGrabber} />
            <View style={cashStyles.sheetMobileHeader}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={cashStyles.sheetMobileTitle}>{title}</Text>
                {subtitle ? <Text style={cashStyles.sheetMobileSubtitle}>{subtitle}</Text> : null}
              </View>
              <TouchableOpacity
                style={cashStyles.sheetMobileClose}
                onPress={onClose}
                accessibilityRole="button"
                accessibilityLabel="Cerrar"
              >
                <Text style={cashStyles.sheetMobileCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView
              {...webOperationalScrollBindings({ flexGrow: 0, flexShrink: 1, minHeight: 0 })}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={{
                paddingHorizontal: spacing.md,
                paddingVertical: spacing.xs,
                width: '100%',
              }}
              showsVerticalScrollIndicator
              nestedScrollEnabled
            >
              {children}
            </ScrollView>
            <View style={cashStyles.sheetMobileFooter}>
              <TouchableOpacity
                style={[cashStyles.btnMobileSecondary, { width: '100%' }]}
                onPress={onClose}
                accessibilityRole="button"
              >
                <Text style={cashStyles.btnMobileSecondaryText}>{footerLabel}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  }

  // ── Wide: centered card ──
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} accessibilityViewIsModal>
      <View style={[posUi.modalBackdrop, modalExperience.modalRoot, backdropPadding]}>
        <View
          style={[
            posUi.modalCard,
            modalShellShared.modalCardShell,
            wide ? modalExperience.modalWidthComfortable : modalExperience.modalWidthStandard,
            { maxHeight: '92%', gap: spacing.sm, width: '100%', alignSelf: 'center' },
          ]}
        >
          <ModalScreenHeader title={title} subtitle={subtitle} onClose={onClose} />
          <ScrollView
            {...webOperationalScrollBindings({ flexGrow: 0, flexShrink: 1, minHeight: 0 })}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ paddingVertical: spacing.xs }}
            showsVerticalScrollIndicator
            nestedScrollEnabled
          >
            {children}
          </ScrollView>
          <TouchableOpacity style={styles.secondaryButton} onPress={onClose} accessibilityRole="button">
            <Text style={styles.secondaryButtonText}>{footerLabel}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}
